import { HttpBackend, HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';

import { getFileNameFromContentDisposition } from '@ideals/utils/get-file-name-from-content-disposition';
import { switchMapToVoid } from '@ideals/utils/void';

import { DiagnosticStatus, ErrorDetails, ErrorReport } from '../../models';
import { TROUBLESHOOTING_CONFIG } from '../../troubleshooting.token';

import { Troubleshooting } from './troubleshooting';

interface ErrorReportDetails {
  readonly action: 'DownloadFileContent' | 'DownloadFile';
  readonly fileType: FileType;
}

type FileType =
  | 'Docx'
  | 'EncryptedDocx'
  | 'EncryptedPdf'
  | 'EncryptedXlsx'
  | 'EncryptedZip'
  | 'PasswordProtectedXlsx'
  | 'Pdf'
  | 'Xlsx'
  | 'Zip';

const FILE_CONTENT_TYPES: FileType[] = ['Pdf', 'Xlsx'];
const FILE_TYPES: FileType[] = [
  'Docx',
  'EncryptedDocx',
  'EncryptedPdf',
  'EncryptedXlsx',
  'EncryptedZip',
  'PasswordProtectedXlsx',
  'Pdf',
  'Xlsx',
  'Zip',
];

export class CheckFilesService extends Troubleshooting {
  readonly #config = inject(TROUBLESHOOTING_CONFIG);
  readonly #httpClient = new HttpClient(inject(HttpBackend));
  readonly #translateService = inject(TranslateService);

  #errorFileTypes: FileType[] = [];
  #errorReports: ErrorReport<ErrorReportDetails>[] = [];
  #status: DiagnosticStatus = 'Default';

  override getErrorDetails(): ErrorDetails {
    return {
      title: this.#translateService.instant('help_modal.TEXT.We_couldn\'t') as string,
      description: this.#translateService.instant('help_modal.TEXT.Check_that_your') as string,
      icon: 'icon icon-note-void',
      values: this.#errorFileTypes.join('; '),
      dataIdeals: 'copy-files-issues',
    };
  }

  override getErrorReports(): { [key: string]: ErrorReport<ErrorReportDetails>[]; } {
    return { files: this.#errorReports };
  }

  override getStatus(): DiagnosticStatus {
    return this.#status;
  }

  override run(): Observable<void> {
    this.#status = 'InProgress';

    const allChecks$ = this.#config.fileStorageUrls.flatMap((baseUrl) => {
      const checkFileContentTypes$ = FILE_CONTENT_TYPES.map((fileType) => {
        return this.#downloadFileContent(baseUrl, fileType)
          .pipe(
            map(() => null),
            catchError((error: HttpErrorResponse) => of(
              this.#createErrorReport(error, fileType, 'DownloadFileContent'),
            )),
          );
      });

      const checkFileTypes$ = FILE_TYPES.map((fileType) => {
        return this.#downloadFile(baseUrl, fileType)
          .pipe(
            switchMap((response) => {
              const fileName = getFileNameFromContentDisposition(response.headers.get('content-disposition')!);

              return this.#uploadFile(
                baseUrl,
                fileType,
                fileName,
                response.body!,
              );
            }),
            map(() => null),
            catchError((error: HttpErrorResponse) => of(
              this.#createErrorReport(error, fileType, 'DownloadFile'),
            )),
          );
      });

      return [...checkFileContentTypes$, ...checkFileTypes$];
    });

    return forkJoin(allChecks$)
      .pipe(
        tap((reports: (ErrorReport<ErrorReportDetails> | null)[]) => {
          this.#errorReports = reports.filter((r) => r != null);
          this.#errorFileTypes = Array.from(new Set(this.#errorReports.map((r) => r.details.fileType)));
          this.#status = this.#errorReports.length ? 'Error' : 'Completed';
        }),
        switchMapToVoid,
      );
  }

  #createErrorReport(
    error: HttpErrorResponse,
    fileType: FileType,
    action: ErrorReport<ErrorReportDetails>['details']['action'],
  ): ErrorReport<ErrorReportDetails> {
    const errorBaseReport = this.createErrorBaseReport(error);

    return {
      ...errorBaseReport,
      details: { action, fileType },
    };
  }

  #downloadFile(baseUrl: string, fileType: FileType): Observable<HttpResponse<Blob>> {
    return this.#httpClient.post(`${baseUrl}/api/v1/connectivity/${fileType}/download`, null, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  #downloadFileContent(baseUrl: string, fileType: FileType): Observable<Blob> {
    return this.#httpClient.get(`${baseUrl}/api/v1/connectivity/${fileType}/content`, { responseType: 'blob' });
  }

  #uploadFile(baseUrl: string, fileType: FileType, fileName: string, blob: Blob): Observable<string> {
    return this.#httpClient.post(`${baseUrl}/api/v1/connectivity/${fileType}/upload`, blob, {
      headers: {
        'Content-Disposition': `attachment; filename="${encodeURIComponent(fileName)}"`,
        'Content-Type': 'application/octet-stream',
      },
      responseType: 'text',
    });
  }
}
